import React, { useEffect, useState } from 'react';
import { Box, Typography, Card, CardContent, Grid } from '@mui/material';
import { Bar, Line, Doughnut } from 'react-chartjs-2';
import {
    Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title,
    Tooltip, Legend, LineElement, PointElement, ArcElement,
} from 'chart.js';
import { getFirestore, collection, getDocs } from 'firebase/firestore';

ChartJS.register(
    CategoryScale, LinearScale, BarElement, Title,
    Tooltip, Legend, LineElement, PointElement, ArcElement
);

const NAVY = '#011838';
const BLUE = '#003E87';
const GOLD = '#FDC500';

const StatCard = ({ title, value, subtitle, color, light }) => (
    <Card
        elevation={0}
        sx={{
            border: '1px solid #e8eaf0',
            borderRadius: 2,
            borderTop: `4px solid ${color}`,
            height: '100%',
        }}
    >
        <CardContent sx={{ p: 2.5, pb: '20px !important' }}>
            <Typography
                variant="caption"
                sx={{ color: '#999', textTransform: 'uppercase', letterSpacing: 1.2, fontSize: 10, fontWeight: 600 }}
            >
                {title}
            </Typography>
            <Typography
                variant="h3"
                sx={{ color: color, fontWeight: 800, mt: 1, fontSize: { xs: 32, sm: 38 }, lineHeight: 1 }}
            >
                {value}
            </Typography>
            {subtitle && (
                <Typography variant="body2" sx={{ color: '#aaa', mt: 1, fontSize: 12 }}>
                    {subtitle}
                </Typography>
            )}
        </CardContent>
    </Card>
);

const chartBaseOptions = {
    responsive: true,
    plugins: {
        legend: { position: 'top', labels: { font: { size: 12 }, padding: 16 } },
    },
    scales: {
        y: {
            beginAtZero: true,
            ticks: { precision: 0, font: { size: 11 } },
            grid: { color: '#f0f0f0' },
        },
        x: {
            ticks: { font: { size: 11 } },
            grid: { display: false },
        },
    },
};

export default function Painel() {
    const [documentData, setDocumentData] = useState({ labels: [], counts: [] });
    const [totalDocuments, setTotalDocuments] = useState(0);
    const [typeCounts, setTypeCounts] = useState({});
    const [statusCounts, setStatusCounts] = useState({ 'Em análise': 0, Concluído: 0 });

    useEffect(() => {
        const fetchDocumentData = async () => {
            const db = getFirestore();
            const querySnapshot = await getDocs(collection(db, 'ouvidoriarh'));

            const monthData = [];
            let total = 0;
            const typeCount = {};
            const statusCount = { 'Em análise': 0, Concluído: 0 };

            querySnapshot.forEach((doc) => {
                const d = doc.data();
                const month = new Date(d.criacao).toLocaleString('pt-BR', { month: 'short', year: 'numeric' });
                monthData.push(month);
                if (d.tipo) typeCount[d.tipo] = (typeCount[d.tipo] || 0) + 1;
                if (d.status) statusCount[d.status] = (statusCount[d.status] || 0) + 1;
                total++;
            });

            const monthCount = {};
            monthData.forEach(m => { monthCount[m] = (monthCount[m] || 0) + 1; });

            setDocumentData({ labels: Object.keys(monthCount), counts: Object.values(monthCount) });
            setTotalDocuments(total);
            setTypeCounts(typeCount);
            setStatusCounts(statusCount);
        };

        fetchDocumentData();
    }, []);

    const emAnalise = statusCounts['Em análise'] || 0;
    const concluidos = statusCounts['Concluído'] || 0;
    const taxaResolucao = totalDocuments > 0
        ? Math.round((concluidos / totalDocuments) * 100)
        : 0;

    return (
        <Box sx={{ p: { xs: 2, sm: 3 } }}>
            {/* Page header */}
            <Box sx={{ mb: 3 }}>
                <Typography variant="h5" sx={{ color: NAVY, fontWeight: 700, letterSpacing: 0.3 }}>
                    Dashboard
                </Typography>
                <Typography variant="body2" sx={{ color: '#888', mt: 0.5, fontSize: 13 }}>
                    Visão geral das respostas da ouvidoria
                </Typography>
            </Box>

            {/* KPI Cards */}
            <Grid container spacing={2.5} sx={{ mb: 3 }}>
                <Grid item xs={6} sm={3}>
                    <StatCard
                        title="Total de Registros"
                        value={totalDocuments}
                        subtitle="Todos os períodos"
                        color={NAVY}
                    />
                </Grid>
                <Grid item xs={6} sm={3}>
                    <StatCard
                        title="Em Análise"
                        value={emAnalise}
                        subtitle="Aguardando resolução"
                        color="#F57C00"
                    />
                </Grid>
                <Grid item xs={6} sm={3}>
                    <StatCard
                        title="Concluídos"
                        value={concluidos}
                        subtitle="Resolvidos"
                        color="#388E3C"
                    />
                </Grid>
                <Grid item xs={6} sm={3}>
                    <StatCard
                        title="Taxa de Resolução"
                        value={`${taxaResolucao}%`}
                        subtitle="Concluídos / Total"
                        color={GOLD}
                    />
                </Grid>
            </Grid>

            {/* Type breakdown (if more than one type) */}
            {Object.keys(typeCounts).length > 0 && (
                <Box sx={{ mb: 3 }}>
                    <Typography variant="caption" sx={{ color: '#999', textTransform: 'uppercase', letterSpacing: 1.2, fontSize: 10, fontWeight: 600 }}>
                        Registros por Tipo
                    </Typography>
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        {Object.entries(typeCounts).map(([type, count]) => (
                            <Grid item xs={6} sm={3} key={type}>
                                <Card
                                    elevation={0}
                                    sx={{
                                        border: '1px solid #e8eaf0',
                                        borderRadius: 2,
                                        textAlign: 'center',
                                        py: 1.5,
                                    }}
                                >
                                    <Typography variant="h5" sx={{ color: BLUE, fontWeight: 700 }}>{count}</Typography>
                                    <Typography variant="caption" sx={{ color: '#888', fontSize: 12 }}>{type}</Typography>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </Box>
            )}

            {/* Charts row */}
            <Grid container spacing={3}>
                {/* Bar chart */}
                <Grid item xs={12} md={7}>
                    <Card elevation={0} sx={{ border: '1px solid #e8eaf0', borderRadius: 2, p: 2.5 }}>
                        <Typography variant="subtitle2" sx={{ color: NAVY, fontWeight: 700, mb: 2, fontSize: 13 }}>
                            Registros por Período
                        </Typography>
                        <Bar
                            data={{
                                labels: documentData.labels,
                                datasets: [{
                                    label: 'Registros',
                                    data: documentData.counts,
                                    backgroundColor: 'rgba(0, 62, 135, 0.75)',
                                    borderColor: BLUE,
                                    borderWidth: 1,
                                    borderRadius: 5,
                                    borderSkipped: false,
                                }],
                            }}
                            options={chartBaseOptions}
                        />
                    </Card>
                </Grid>

                {/* Doughnut status */}
                <Grid item xs={12} md={5}>
                    <Card elevation={0} sx={{ border: '1px solid #e8eaf0', borderRadius: 2, p: 2.5 }}>
                        <Typography variant="subtitle2" sx={{ color: NAVY, fontWeight: 700, mb: 2, fontSize: 13 }}>
                            Distribuição por Status
                        </Typography>
                        <Box sx={{ maxWidth: 260, mx: 'auto' }}>
                            <Doughnut
                                data={{
                                    labels: ['Em Análise', 'Concluídos'],
                                    datasets: [{
                                        data: [emAnalise, concluidos],
                                        backgroundColor: ['#FF9800', '#4CAF50'],
                                        borderColor: ['#F57C00', '#388E3C'],
                                        borderWidth: 2,
                                        hoverOffset: 6,
                                    }],
                                }}
                                options={{
                                    responsive: true,
                                    plugins: {
                                        legend: { position: 'bottom', labels: { font: { size: 12 }, padding: 16 } },
                                    },
                                    cutout: '68%',
                                }}
                            />
                        </Box>
                    </Card>
                </Grid>

                {/* Line chart full width */}
                <Grid item xs={12}>
                    <Card elevation={0} sx={{ border: '1px solid #e8eaf0', borderRadius: 2, p: 2.5 }}>
                        <Typography variant="subtitle2" sx={{ color: NAVY, fontWeight: 700, mb: 2, fontSize: 13 }}>
                            Tendência Mensal
                        </Typography>
                        <Line
                            data={{
                                labels: documentData.labels,
                                datasets: [{
                                    label: 'Registros',
                                    data: documentData.counts,
                                    borderColor: GOLD,
                                    backgroundColor: 'rgba(253, 197, 0, 0.12)',
                                    borderWidth: 2.5,
                                    pointBackgroundColor: GOLD,
                                    pointBorderColor: '#fff',
                                    pointBorderWidth: 2,
                                    pointRadius: 5,
                                    fill: true,
                                    tension: 0.4,
                                }],
                            }}
                            options={chartBaseOptions}
                        />
                    </Card>
                </Grid>
            </Grid>

            {/* Footer */}
            <Box sx={{ mt: 4, textAlign: 'center' }}>
                <Typography variant="caption" sx={{ color: '#ccc', fontSize: 11 }}>
                    © {new Date().getFullYear()} Metrum — memt.com.br
                </Typography>
            </Box>
        </Box>
    );
}
